import { EDITION_CAPABILITIES, type EditionCapabilities, type MetaDto } from '@dbrain/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from './api/client.js';
import { setProductEdition } from './i18n/index.js';
import { experienceFor, type Experience } from '@dbrain/app-core/origins';

/**
 * One platform, two entry addresses (decision 2026-10-27): the chat and the
 * code experience are the same app on two origins. The app picks its
 * experience from the address it was opened on; with a single address (no
 * CODE_ORIGIN, or a development server) the last choice is remembered in
 * the browser.
 */
export { experienceFor, type Experience };

const STORAGE_KEY = 'dbrain.experience';

function storedChoice(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function useMeta() {
  return useQuery({
    queryKey: ['meta'],
    queryFn: async () => {
      const meta = await api<MetaDto>('GET', '/api/meta');
      // the products' names of this edition, before any screen shows them (D-217)
      setProductEdition(meta.edition);
      return meta;
    },
    staleTime: Infinity,
  });
}

/** an API without capabilities is the bireysel edition (the shared table, ADR-0023) */
const BIREYSEL: EditionCapabilities = EDITION_CAPABILITIES.bireysel;

/** Phase 13d: what this edition offers (sign-up, invitations, company sign-in…); code asks for a capability, never for an edition. */
export function useCapabilities(): EditionCapabilities {
  return useMeta().data?.capabilities ?? BIREYSEL;
}

export function useExperience(): Experience {
  const meta = useMeta();
  // Phase 16a: an edition without Code is the chat on every address
  if (meta.data?.capabilities && !meta.data.capabilities.codeExperience) return 'chat';
  return experienceFor(meta.data, window.location.origin, storedChoice());
}

/**
 * Where a path opens in the other experience: the other origin when there is
 * one (one login covers both), otherwise the same app with the choice
 * remembered.
 */
export function switchTarget(meta: MetaDto | undefined, current: Experience, path: string): string {
  const target: Experience = current === 'chat' ? 'code' : 'chat';
  const origin = target === 'code' ? meta?.codeOrigin : meta?.chatOrigin;
  if (meta?.codeOrigin && origin && origin !== window.location.origin) return `${origin}${path}`;
  try {
    localStorage.setItem(STORAGE_KEY, target);
  } catch {
    // private mode: the choice lasts for this page only
  }
  return path;
}

/** The address a conversation or workspace of a kind belongs on, when it is not this one. */
export function homeOf(meta: MetaDto | undefined, kind: Experience): string | null {
  if (!meta?.codeOrigin) return null;
  const origin = kind === 'code' ? meta.codeOrigin : meta.chatOrigin;
  return origin === window.location.origin ? null : origin;
}
